import React from 'react';
import { Box, Typography } from '@mui/material';
import AgentAvatar from '../pages/AgentAvatar';
import InlineAgentCard, { AGENT_MODEL_LABELS } from '../pages/InlineAgentCard';
import NodeAutocomplete from './NodeAutocomplete';

/**
 * NodeAgentPicker — searchable agent selector with avatar-rich dropdown options
 * and an optional InlineAgentCard summary. Used anywhere an agentId must be
 * chosen (AI Agent column config, board stages, workflow nodes, gates).
 *
 * Pass `agents` from the caller (typically filtered non-system agents from
 * GET project-service/agent/list). Supports `inDialog` / `canvas` like other
 * NodeUI pickers.
 */
export function renderAgentPickerOption(props, option) {
  if (!option?.agentId) return null;

  const modelLabel = AGENT_MODEL_LABELS[option.model] || option.model || 'No model';
  const toolCount = option.enabledTools ? option.enabledTools.length : 0;

  return (
    <li {...props} key={option.agentId}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.5 }}>
        <AgentAvatar
          name={option.name}
          size={32}
          borderRadius="8px"
          systemAgent={option.systemAgent}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
            noWrap
          >
            {option.name || 'Untitled Agent'}
          </Typography>
          <Typography
            sx={{ fontSize: '11.5px', color: 'text.secondary', lineHeight: 1.3 }}
            noWrap
          >
            {option.description || 'No description'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
              {modelLabel}
            </Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'divider' }}>·</Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
              {toolCount > 0 ? `${toolCount} tool${toolCount !== 1 ? 's' : ''}` : 'No tools'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </li>
  );
}

const NodeAgentPicker = React.memo(function NodeAgentPicker({
  label = 'Agent',
  value = null,
  onChange,
  agents = [],
  loading = false,
  loadingText = 'Loading agents...',
  noOptionsText = 'No agents found',
  placeholder = 'Select an agent...',
  disabled = false,
  inDialog = false,
  canvas = false,
  showCard = true,
  compactCard = true,
  onConfigure,
  renderOption = renderAgentPickerOption,
  getOptionLabel = (option) => option?.name || 'Untitled',
}) {
  return (
    <>
      <NodeAutocomplete
        label={label}
        value={value}
        onChange={onChange}
        options={agents}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, val) => option?.agentId === val?.agentId}
        renderOption={renderOption}
        placeholder={placeholder}
        loading={loading}
        loadingText={loadingText}
        noOptionsText={noOptionsText}
        disabled={disabled}
        inDialog={inDialog}
        canvas={canvas}
      />
      {showCard && value ? (
        <InlineAgentCard agent={value} compact={compactCard} onConfigure={onConfigure} />
      ) : null}
    </>
  );
});

export default NodeAgentPicker;
